import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { ApiUrl } from "Services/Services";

function EditDeal() {
  const { id } = useParams();
  const [foods, setFoods] = useState([]);
  const [selectedFoods, setSelectedFoods] = useState([]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    status: "active",
  });

  // Fetch all foods with variations
  useEffect(() => {
    async function fetchFoods() {
      const res = await axios.get(`${ApiUrl}/food`);
      setFoods(res.data.foods || []);
    }
    fetchFoods();
  }, []);

  // Fetch deal by ID
  useEffect(() => {
    async function fetchDeal() {
      try {
        const res = await axios.get(`${ApiUrl}/deals/${id}`);
        const deal = res.data;
        setForm({
          name: deal.name,
          description: deal.description,
          price: deal.price,
          status: deal.status,
        });
        setImagePreview(deal.image);

        // Map existing deal foods (include variationId)
        setSelectedFoods(
          deal.foods.map((f) => ({
            foodId: f.id,
            quantity: f.DealItem.quantity,
            variationId: f.DealItem.variationId || null,
          }))
        );
      } catch (err) {
        console.error(err);
      }
    }
    fetchDeal();
  }, [id]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleFoodToggle = (food) => {
    if (selectedFoods.find((f) => f.foodId === food.id)) {
      setSelectedFoods(selectedFoods.filter((f) => f.foodId !== food.id));
    } else {
      setSelectedFoods([
        ...selectedFoods,
        { foodId: food.id, quantity: 1, variationId: null },
      ]);
    }
  };

  const handleQuantityChange = (foodId, quantity) => {
    setSelectedFoods(
      selectedFoods.map((f) =>
        f.foodId === foodId ? { ...f, quantity: parseInt(quantity) } : f
      )
    );
  };

  const handleVariationChange = (foodId, variationId) => {
    setSelectedFoods(
      selectedFoods.map((f) =>
        f.foodId === foodId ? { ...f, variationId: parseInt(variationId) } : f
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append("name", form.name);
    data.append("description", form.description);
    data.append("price", form.price);
    data.append("status", form.status);
    if (image) data.append("image", image);
    data.append("foods", JSON.stringify(selectedFoods));

    try {
      const res = await axios.put(`${ApiUrl}/deals/${id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Deal updated successfully!");
      console.log(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to update deal.");
    }
  };

  return (
    <div className="max-w-4xl mx-auto mt-10 bg-white shadow-lg rounded-xl p-6 space-y-6">
      <h2 className="text-2xl font-bold mb-4">Edit Deal</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Deal Name */}
        <div>
          <label className="block font-semibold mb-1">Deal Name</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block font-semibold mb-1">Description</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          />
        </div>

        {/* Price */}
        <div>
          <label className="block font-semibold mb-1">Price</label>
          <input
            type="number"
            name="price"
            value={form.price}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            required
          />
        </div>

        {/* Status */}
        <div>
          <label className="block font-semibold mb-1">Status</label>
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Image Upload */}
        <div>
          <label className="block font-semibold mb-1">Deal Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="mb-2"
          />
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Preview"
              className="w-40 h-40 object-cover rounded border"
            />
          )}
        </div>

        {/* Food List */}
        <div>
          <h3 className="font-semibold mb-2">Select Foods</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-64 overflow-y-auto border p-2 rounded">
            {foods.map((food) => {
              const isSelected = selectedFoods.find((f) => f.foodId === food.id);
              return (
                <div key={food.id} className="flex flex-col border p-2 rounded">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={!!isSelected}
                      onChange={() => handleFoodToggle(food)}
                    />
                    <img
                      src={food.image}
                      alt={food.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{food.name}</p>
                      <p className="text-sm text-gray-500">
                        ${food.price}
                      </p>
                    </div>
                  </div>

                  {isSelected && (
                    <div className="ml-6 mt-2 flex items-center space-x-2">
                      {/* Quantity */}
                      <input
                        type="number"
                        value={isSelected.quantity}
                        min={1}
                        onChange={(e) =>
                          handleQuantityChange(food.id, e.target.value)
                        }
                        className="w-16 border p-1 rounded"
                      />

                      {/* Variation Dropdown */}
                      {food.variations && food.variations.length > 0 && (
                        <select
                          value={isSelected.variationId || ""}
                          onChange={(e) =>
                            handleVariationChange(food.id, e.target.value)
                          }
                          className="border p-1 rounded"
                        >
                          <option value="">Select Variation</option>
                          {food.variations.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.name} (${v.price})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Update Deal
        </button>
      </form>
    </div>
  );
}

export default EditDeal;
